import { supabase } from "./supabaseClient";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function token() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

// Short-lived GET de-duplication: identical GETs issued within the same page flow (e.g. /me from the
// sign-in check and again from the auth-state listener; /viewer/preferences from two mounts) share one
// response. Only successful GETs are reused, and only for ~1.5 s, so refresh/error semantics are kept.
const _recentGets = new Map();
const GET_REUSE_MS = 1500;

export async function api(path, { method = "GET", body, auth = true } = {}) {
  if (method === "GET") {
    const hit = _recentGets.get(path);
    if (hit && Date.now() - hit.at < GET_REUSE_MS) return hit.promise;
    const promise = _request(path, { method, body, auth }).then((r) => {
      if (!r.ok) _recentGets.delete(path);
      return r;
    });
    _recentGets.set(path, { at: Date.now(), promise });
    return promise;
  }
  // Any mutation invalidates reuse so a follow-up GET always reflects the write.
  _recentGets.clear();
  return _request(path, { method, body, auth });
}

async function _request(path, { method, body, auth }) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const t = await token();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

export async function uploadFile(file) {
  const t = await token();
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${BASE}/uploads`, {
    method: "POST",
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    body: fd,
  });
  return { ok: res.ok, status: res.status, data: await res.json().catch(() => ({})) };
}

// XMLHttpRequest (not fetch) so the browser's upload-progress events can tell "Uploading" (bytes still
// leaving the device) from "Processing" (body fully sent; server decoding/encoding/storing). No polling.
export async function uploadPhoto(file, onStage) {
  const t = await token();
  const fd = new FormData();
  fd.append("file", file);
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/photos`);
    if (t) xhr.setRequestHeader("Authorization", `Bearer ${t}`);
    xhr.upload.onprogress = (e) => {
      if (!onStage) return;
      if (e.lengthComputable && e.loaded >= e.total) onStage("processing", 100);
      else onStage("uploading", e.lengthComputable ? Math.round((100 * e.loaded) / e.total) : null);
    };
    xhr.upload.onload = () => onStage && onStage("processing", 100);
    xhr.onerror = () => resolve({ ok: false, status: 0, data: {} });
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText || "{}"); } catch (_e) {}
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data,
                serverTiming: xhr.getResponseHeader("Server-Timing") });
    };
    onStage && onStage("uploading", 0);
    xhr.send(fd);
  });
}

export async function authedBlobUrl(path) {
  const t = await token();
  const res = await fetch(`${BASE}${path}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

export { BASE };
